import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import { Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/auth/AuthProvider';
import { useDeleteAllFeedback, useDeleteFeedback, useFeedback } from '@/features/feedback/hooks';
import type { FeedbackEntry, FeedbackOrderSnapshot } from '@/api/cockpit';
import { formatRelativeTime } from '@/lib/utils';

type Lane = FeedbackEntry['lane'];

type LaneTone = 'default' | 'warning' | 'success' | 'secondary';

const lanes: readonly { id: Lane; label: string; tone: LaneTone }[] = [
  { id: 'captured', label: 'Captured', tone: 'warning' },
  { id: 'in_training', label: 'In Training', tone: 'default' },
  { id: 'applied', label: 'Applied', tone: 'success' },
  { id: 'superseded', label: 'Superseded', tone: 'secondary' },
];

export function FeedbackPage(): JSX.Element {
  const navigate = useNavigate();
  const { principal } = useAuth();
  const { data, isLoading, isError, error, refetch, isFetching } = useFeedback();
  const canDeleteFeedback =
    principal?.roles.some((role) => role === 'SO-Supervisor' || role === 'SO-Admin') ?? false;
  const canDeleteAll = principal?.roles.includes('SO-Admin') ?? false;

  const grouped = useMemo(() => {
    const out: Record<Lane, FeedbackEntry[]> = {
      captured: [],
      in_training: [],
      applied: [],
      superseded: [],
    };
    (data ?? []).forEach((entry) => {
      // Defensive: if the backend ever returns a lane this component
      // doesn't render, skip it rather than crash.
      const bucket = out[entry.lane];
      if (bucket) bucket.push(entry);
    });
    return out;
  }, [data]);

  return (
    <div className="space-y-6">
      <div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Feedback tracker</h1>
            <p className="text-sm text-muted-foreground">
              Operator corrections flow Captured → In Training → Applied. The on-close curator marks
              stale corrections Superseded; the nightly corpus updater (T-703) promotes the rest.
            </p>
          </div>
          {canDeleteAll ? (
            <FeedbackDeleteAllDialog
              count={data?.length ?? 0}
              onDeleted={() => void refetch()}
            />
          ) : null}
        </div>
      </div>

      <FeedbackTrackerBody
        grouped={grouped}
        isLoading={isLoading}
        isError={isError}
        errorMessage={error instanceof Error ? error.message : 'Unknown error.'}
        isFetching={isFetching}
        onRetry={() => void refetch()}
        onOpen={(stagingRowId) => navigate(`/exceptions/${stagingRowId}`)}
        canDeleteFeedback={canDeleteFeedback}
      />
    </div>
  );
}

interface FeedbackTrackerBodyProps {
  grouped: Record<Lane, FeedbackEntry[]>;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
  isFetching: boolean;
  onRetry: () => void;
  onOpen: (stagingRowId: string) => void;
  canDeleteFeedback: boolean;
}

function FeedbackTrackerBody(props: FeedbackTrackerBodyProps): JSX.Element {
  if (props.isLoading) {
    return <FeedbackLoadingState />;
  }
  if (props.isError) {
    return (
      <FeedbackErrorState
        errorMessage={props.errorMessage}
        isFetching={props.isFetching}
        onRetry={props.onRetry}
      />
    );
  }
  return (
    <FeedbackLaneGrid
      grouped={props.grouped}
      onOpen={props.onOpen}
      canDeleteFeedback={props.canDeleteFeedback}
    />
  );
}

function FeedbackLoadingState(): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-80 w-full" />
      ))}
    </div>
  );
}

function FeedbackErrorState(props: {
  errorMessage: string;
  isFetching: boolean;
  onRetry: () => void;
}): JSX.Element {
  return (
    <Card>
      <CardContent className="flex items-center justify-between gap-4 py-8">
        <div className="text-sm text-muted-foreground">
          Failed to load feedback records. {props.errorMessage}
        </div>
        <Button variant="outline" size="sm" onClick={props.onRetry} disabled={props.isFetching}>
          Retry
        </Button>
      </CardContent>
    </Card>
  );
}

function FeedbackLaneGrid(props: {
  grouped: Record<Lane, FeedbackEntry[]>;
  onOpen: (stagingRowId: string) => void;
  canDeleteFeedback: boolean;
}): JSX.Element {
  // LayoutGroup keeps each card's layoutId in scope across columns so a card
  // moving from "captured" to "superseded" tweens its position smoothly.
  return (
    <LayoutGroup>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {lanes.map((lane) => (
          <FeedbackLaneColumn
            key={lane.id}
            lane={lane}
            entries={props.grouped[lane.id]}
            onOpen={props.onOpen}
            canDeleteFeedback={props.canDeleteFeedback}
          />
        ))}
      </div>
    </LayoutGroup>
  );
}

function FeedbackLaneColumn(props: {
  lane: (typeof lanes)[number];
  entries: readonly FeedbackEntry[];
  onOpen: (stagingRowId: string) => void;
  canDeleteFeedback: boolean;
}): JSX.Element {
  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">{props.lane.label}</CardTitle>
        <Badge variant={props.lane.tone === 'default' ? 'secondary' : props.lane.tone}>
          {props.entries.length}
        </Badge>
      </CardHeader>
      <CardContent className="flex-1 space-y-2">
        {props.entries.length === 0 ? (
          <EmptyFeedbackLane />
        ) : (
          <AnimatePresence mode="popLayout" initial={false}>
            {props.entries.map((entry) => (
              <FeedbackEntryCard
                key={entry.id}
                entry={entry}
                onOpen={props.onOpen}
                canDeleteFeedback={props.canDeleteFeedback}
              />
            ))}
          </AnimatePresence>
        )}
      </CardContent>
    </Card>
  );
}

function FeedbackEntryCard(props: {
  entry: FeedbackEntry;
  onOpen: (stagingRowId: string) => void;
  canDeleteFeedback: boolean;
}): JSX.Element {
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const snapshot = props.entry.orderSnapshot;
  return (
    <motion.div
      // layoutId stays stable across lane changes so framer-motion FLIPs
      // the card from its old column position to its new one.
      layoutId={`feedback-${props.entry.id}`}
      layout
      initial={{ opacity: 0, y: -6, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 6, scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 320, damping: 28, mass: 0.6 }}
      className="rounded-md border bg-card/70 p-3 text-sm shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="whitespace-pre-line font-medium leading-relaxed">{props.entry.summary}</div>
      <div className="mt-1 text-xs text-muted-foreground">
        {props.entry.field} · {props.entry.operator} · {formatRelativeTime(props.entry.capturedAt)}
      </div>
      {snapshot ? (
        <div className="mt-3 rounded-md border bg-muted/30 p-2 text-xs">
          <button
            type="button"
            className="flex w-full items-center justify-between text-left font-medium text-foreground"
            onClick={() => setSnapshotOpen((open) => !open)}
            aria-expanded={snapshotOpen}
          >
            <span>Order context</span>
            <span className="text-muted-foreground">{snapshotOpen ? 'Hide' : 'Show'}</span>
          </button>
          {snapshotOpen ? <OrderSnapshotDetails snapshot={snapshot} /> : null}
        </div>
      ) : null}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {props.entry.stagingRowId ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              if (props.entry.stagingRowId) props.onOpen(props.entry.stagingRowId);
            }}
          >
            Open
          </Button>
        ) : (
          <span className="text-xs italic text-muted-foreground">
            Source row deleted · feedback retained for matching
          </span>
        )}
        {props.canDeleteFeedback ? <FeedbackDeleteDialog entry={props.entry} /> : null}
      </div>
    </motion.div>
  );
}

function FeedbackDeleteDialog(props: { entry: FeedbackEntry }): JSX.Element {
  const [open, setOpen] = useState(false);
  const deleteFeedback = useDeleteFeedback();
  return (
    <>
      <Button
        type="button"
        variant="destructive"
        size="sm"
        onClick={() => setOpen(true)}
        disabled={deleteFeedback.isPending}
      >
        <Trash2 className="h-4 w-4" />
        Delete
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete feedback</DialogTitle>
            <DialogDescription>
              This permanently deletes this feedback record and any matching hint indexed from it.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border bg-muted/30 p-3 text-sm">
            <div className="font-medium">{props.entry.field}</div>
            <div className="mt-1 text-muted-foreground">{props.entry.summary}</div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={deleteFeedback.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                deleteFeedback.mutate(props.entry.id, {
                  onSuccess: () => setOpen(false),
                });
              }}
              disabled={deleteFeedback.isPending}
            >
              <Trash2 className="h-4 w-4" />
              Permanently delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function FeedbackDeleteAllDialog(props: { count: number; onDeleted: () => void }): JSX.Element {
  const [open, setOpen] = useState(false);
  const deleteAllFeedback = useDeleteAllFeedback();
  return (
    <>
      <Button
        type="button"
        variant="destructive"
        size="sm"
        onClick={() => setOpen(true)}
        disabled={props.count === 0 || deleteAllFeedback.isPending}
      >
        <Trash2 className="h-4 w-4" />
        Delete All
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete all feedback</DialogTitle>
            <DialogDescription>
              This permanently deletes all feedback records and their matching hints. This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border bg-muted/30 p-3 text-sm">
            <span className="font-medium">{props.count}</span> feedback records will be deleted.
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={deleteAllFeedback.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                deleteAllFeedback.mutate(undefined, {
                  onSuccess: () => {
                    setOpen(false);
                    props.onDeleted();
                  },
                });
              }}
              disabled={props.count === 0 || deleteAllFeedback.isPending}
            >
              <Trash2 className="h-4 w-4" />
              Permanently delete all
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function OrderSnapshotDetails(props: { snapshot: FeedbackOrderSnapshot }): JSX.Element {
  const { snapshot } = props;
  const headerRows: Array<[string, string | null]> = [
    ['PO #', snapshot.poNumber],
    ['Customer', joinNonNull(snapshot.customerNumber, snapshot.customerName)],
    ['Supplier', snapshot.supplierName],
    ['Ship-to', joinNonNull(snapshot.shipToName, snapshot.shipToAddress)],
    ['Payment terms', snapshot.paymentTermsText],
    ['Delivery terms', snapshot.deliveryTermsText],
    ['Delivery mode', snapshot.deliveryModeText],
    ['Requested delivery', snapshot.requestedDeliveryDate],
  ];
  const visibleRows = headerRows.filter(([, value]) => value && value.length > 0);
  return (
    <div className="mt-2 space-y-2">
      {visibleRows.length > 0 ? (
        <dl className="grid grid-cols-[max-content,1fr] gap-x-3 gap-y-1">
          {visibleRows.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="break-words text-foreground">{value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {snapshot.lineItems.length > 0 ? (
        <div>
          <div className="mb-1 text-muted-foreground">Touched line(s)</div>
          <ul className="space-y-1">
            {snapshot.lineItems.map((line) => (
              <li key={line.lineIndex} className="rounded border bg-background/60 p-1.5">
                <div className="font-medium text-foreground">
                  Line {line.lineIndex + 1}
                  {line.itemCode ? ` — ${line.itemCode}` : ''}
                </div>
                {line.description ? (
                  <div className="text-muted-foreground">{line.description}</div>
                ) : null}
                <div className="text-muted-foreground">
                  {line.quantity !== null ? `qty ${line.quantity}` : null}
                  {line.unitOfMeasure ? ` ${line.unitOfMeasure}` : null}
                  {line.supplierPartNumber ? ` · supplier part ${line.supplierPartNumber}` : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function joinNonNull(...parts: ReadonlyArray<string | null>): string | null {
  const kept = parts.filter((p): p is string => Boolean(p && p.length > 0));
  return kept.length === 0 ? null : kept.join(' — ');
}

function EmptyFeedbackLane(): JSX.Element {
  return (
    <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
      empty
    </div>
  );
}
